import type { DocumentType } from "@aws-sdk/types";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";
// ResolveSchemaCommand is exported by the generated TypeScript client
// from the Smithy model that includes the ResolveSchema operation.
import { ResolveSchemaCommand } from "@amzn/ttechclaimintakeservice-client";

import { getAuthenticatedClient } from "src/api/setup/getAuthenticatedClient";
import { MetricsPublisher } from "src/metrics/metrics";

/**
 * Input payload for the `/resolveSchema` endpoint.
 *
 * The service uses `lineOfBusiness` plus optional `stage` and `options`
 * to resolve the correct schema via a server-side routing config, then
 * returns the compiled schema directly.
 */
export interface ResolveSchemaInput {
  lineOfBusiness: string;
  stage?: string;
  options?: Record<string, unknown>;
}

/**
 * Successful result from the `/resolveSchema` operation.
 *
 * Contains the compiled JSON Schema for form rendering and the unique
 * `schemaId` that must be forwarded to `/createClaim` so the backend
 * knows which schema version validated the submitted data.
 */
export interface ResolveSchemaResult {
  schema: CloudscapeJsonSchema;
  schemaId: string;
}

/**
 * Calls the `/resolveSchema` operation on the TTechClaimIntakeService
 * using the generated Coral client, identical to how `createClaim` works.
 *
 * Uses `getAuthenticatedClient()` for authorization, sends a
 * `ResolveSchemaCommand`, and returns the resolved schema document along
 * with the `schemaId` needed by `createClaim`.
 *
 * An optional `AbortSignal` can be passed to cancel the in-flight
 * request (e.g. on component unmount). When aborted, the function
 * re-throws the `AbortError` so callers can distinguish cancellations
 * from real failures.
 *
 * Publishes metrics on success/failure following the same pattern as
 * `schemaClaimsService.ts`.
 */
export async function resolveSchema(
  input: ResolveSchemaInput,
  signal?: AbortSignal
): Promise<ResolveSchemaResult> {
  const metricsPublisher = new MetricsPublisher("api.schemas.resolveSchema");

  const { client } = await getAuthenticatedClient();
  if (!client) {
    throw new Error("Schema resolution failed: authentication token is unavailable.");
  }

  const command = new ResolveSchemaCommand({
    lineOfBusiness: input.lineOfBusiness,
    stage: input.stage,
    options: input.options as DocumentType,
  });

  let response: {
    $metadata?: { httpStatusCode?: number };
    schema?: DocumentType;
    schemaId?: string;
  };
  try {
    response = await client.send(command, {
      abortSignal: signal as unknown as import("@aws-sdk/types").AbortSignal,
    });
  } catch (error: unknown) {
    // Re-throw abort errors without publishing failure metrics so
    // callers (e.g. RTK thunks) can distinguish intentional
    // cancellations from real service failures.
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }
    metricsPublisher.publishRenderFatalMetrics();

    throw error;
  }

  if (!response.schema || typeof response.schema !== "object" || !response.schemaId) {
    metricsPublisher.publishRenderFatalMetrics();

    throw new Error(
      `Schema resolution for "${input.lineOfBusiness}" returned a malformed response.`
    );
  }

  // The response must be a valid JSON Schema with at minimum a `type`
  // property. Without this the UI schema generator cannot produce a
  // renderable form and the page would crash with a blank screen.
  const schemaDoc = response.schema as Record<string, unknown>;
  if (!schemaDoc.type) {
    metricsPublisher.publishRenderFatalMetrics();

    throw new Error(
      `Schema "${response.schemaId}" is not a valid JSON Schema for form rendering ` +
        `(missing "type"). The server may have returned metadata instead of a form schema.`
    );
  }

  metricsPublisher.publishRenderSuccessMetrics();

  return {
    schema: response.schema as unknown as CloudscapeJsonSchema,
    schemaId: response.schemaId,
  };
}
